import React from "react";

// Redux
import { withFirebase } from "react-redux-firebase";
import { compose } from "redux";
import { connect } from "react-redux";
import { SET_CLIENTS } from "../actions";

import { history } from "../store";
import { push } from "connected-react-router";

//Router
import { ConnectedRouter } from "connected-react-router";
import Routes from "../routes";

//Styles
import { MuiThemeProvider, createMuiTheme, Button } from "@material-ui/core";

import { defaultTheme } from "../assets/themes";

// Components
import AdminLayout from "./Layout/Admin";
import Loader from "./Loader";
import LoginCard from "./App/LoginCard";

// Interfaces / Contexts / Models
import { IDashboardClient, IBuilding, ISite } from "../interfaces";
import { ThemeContext, DataContext } from "./Contexts";
import { MBuilding } from "../models";

// API
import { tapaAPI } from "../api";

interface Props {
  firebase: { auth: any; profile: any };
  reroute: (route: string) => void;
  clients: IDashboardClient[];
  setClients: any;
}

const App: React.FC<Props> = ({
  firebase: { auth, profile },
  reroute,
  clients,
  setClients,
}) => {
  // Dark mode to feed context
  const [darkMode, setDarkMode] = React.useState(false);
  const [layout, setLayout] = React.useState<any>(AdminLayout);

  // Client Provider
  const [client, setClient] = React.useState({ id: "", name: "", sites: [] });

  // Client Provider
  const [site, setSite] = React.useState({ id: "", name: "", buildings: [] });

  // Default Building Provider
  const [building, setBuilding] = React.useState(MBuilding);

  // Fetching Clients
  const [fetching, setFetching] = React.useState(false);
  const [error, setError] = React.useState({ title: "", message: "" });

  // Memoize Client Selection
  const handleSetClient = React.useCallback(
    (client: IDashboardClient) => {
      // @ts-ignore
      setClient(client);
    },
    [setClient]
  );

  // Memoize Building Selection
  const handleSetSite = React.useCallback(
    (site: ISite) => {
      // @ts-ignore
      setSite(site);
    },
    [setSite]
  );

  // Memoize Building Selection
  const handleSetBuilding = React.useCallback(
    (building: IBuilding) => {
      // @ts-ignore
      setBuilding(building);
    },
    [setBuilding]
  );

  // Fetch Clients
  const fetchClients = () => {
    if (error.title || error.message) {
      setError({ title: "", message: "" });
    }

    setFetching(true);
    tapaAPI({
      route: "clients",
      api: profile.api,
      callback: handleSetClients,
      error: () =>
        setError({
          title: "Error Fetching Buildings",
          message: "This has been logged and we are working on a solution",
        }),
    }).then((res: any) => {
      setFetching(false);
    });
  };

  // fetchingClients callback
  const handleSetClients = (clients: any) => {
    setClients(clients);
    setFetching(false);
  };

  // If user has an API and no clients, load in clients
  React.useEffect(() => {
    if (profile.api && !clients.length && !fetching) {
      fetchClients();
    }
    // eslint-disable-next-line
  }, [profile.api, setClients]);

  // While auth loading, fetching, or awaiting clients - show loader
  if (
    // Loading Auth
    (auth.isEmpty && !auth.isLoaded && profile.isEmpty && !profile.isLoaded) ||
    // Fetching Clients
    fetching ||
    // Awaiting Clients
    (!auth.isEmpty && !Boolean(clients.length))
  ) {
    return (
      <Loader
        title={"Loading "}
        trigger={fetching || !clients.length}
        error={error}
        retry={
          error && (
            <Button onClick={() => fetchClients()} variant="outlined">
              Reconnect
            </Button>
          )
        }
      />
    );
  }

  // If no auth, display login card
  if (auth.isLoaded && auth.isEmpty) {
    return <LoginCard />;
  }

  // Build theme out of whitelabel
  const { whitelabel: { primary = "", secondary = "" } = {} } = profile;

  const theme = createMuiTheme({
    palette: {
      type: darkMode ? "dark" : "light",
      primary: {
        main: primary ? primary : defaultTheme.palette.primary.main,
      },
      secondary: {
        main: secondary ? secondary : defaultTheme.palette.secondary.main,
      },
    },
  });

  return (
    <MuiThemeProvider theme={theme}>
      <ThemeContext.Provider
        value={{ darkMode, setDarkMode, layout, setLayout }}
      >
        <DataContext.Provider
          value={{
            client,
            setClient: handleSetClient,
            site,
            setSite: handleSetSite,
            building,
            setBuilding: handleSetBuilding,
          }}
        >
          <ConnectedRouter history={history}>
            <Routes theme={theme} />
          </ConnectedRouter>
        </DataContext.Provider>
      </ThemeContext.Provider>
    </MuiThemeProvider>
  );
};

export default compose(
  connect(
    (state: any) => ({
      firebase: state.firebase,
      clients: state.api.clients,
    }),
    (dispatch) => ({
      reroute: (route: string) => dispatch(push(route)),
      setClients: (clients: Array<IDashboardClient>) =>
        dispatch({ type: SET_CLIENTS, payload: clients }),
    })
  ),
  withFirebase
)(App);
